import { useCallback, useEffect, useState } from 'react';
import supabase from '../../supabase/supabase';
import { getClientId } from '../../utils/clientIdentity';
import { CHAT_TABLE } from './chatConstants';

const REACTIONS_TABLE = 'chat_reactions_1786721101989';

export default function useChatMessages(currentUser) {
  const [messages, setMessages] = useState([]);
  const [reactions, setReactions] = useState([]);
  const [lastIncomingMessage, setLastIncomingMessage] = useState(null);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      try {
        // Cargar mensajes principales
        const { data: msgsData, error: msgsError } = await supabase
          .from(CHAT_TABLE)
          .select('*')
          .eq('is_deleted', false)
          .order('created_at', { ascending: true })
          .limit(200);

        if (mounted && !msgsError && msgsData) {
          setMessages(msgsData);
        }

        // Cargar reacciones de forma segura (sin fallar si la tabla aún no se ha creado)
        const { data: reactsData, error: reactsError } = await supabase
          .from(REACTIONS_TABLE)
          .select('*')
          .order('created_at', { ascending: true });

        if (mounted && !reactsError && reactsData) {
          setReactions(reactsData);
        }
      } catch (err) {
        console.warn('Carga inicial completada con advertencia:', err);
      }
    };

    loadData();

    // Suscripción Realtime para mensajes
    const messagesChannel = supabase
      .channel('all_chat_messages')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: CHAT_TABLE },
        ({ eventType, new: message }) => {
          if (!mounted) return;
          if (eventType === 'INSERT') {
            setMessages((current) =>
              current.some((item) => item.id === message.id) ? current : [...current, message]
            );
            if (message.nickname !== currentUser) {
              setLastIncomingMessage(message);
            }
          }
          if (eventType === 'UPDATE') {
            setMessages((current) =>
              message.is_deleted
                ? current.filter((item) => item.id !== message.id)
                : current.map((item) => (item.id === message.id ? message : item))
            );
          }
        }
      )
      .subscribe();

    // Suscripción Realtime para reacciones
    const reactionsChannel = supabase
      .channel('all_chat_reactions')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: REACTIONS_TABLE },
        ({ new: reaction }) => {
          if (!mounted) return;
          setReactions((current) => {
            if (current.some((r) => r.id === reaction.id)) return current;
            return [...current, reaction];
          });
        }
      )
      .subscribe();

    return () => {
      mounted = false;
      supabase.removeChannel(messagesChannel);
      supabase.removeChannel(reactionsChannel);
    };
  }, [currentUser]);

  const sendMessage = useCallback(
    async (content, recipient = null) => {
      const nickname = currentUser?.trim();
      const cleanContent = content.trim();
      if (!nickname || !cleanContent || isSending) return false;

      setIsSending(true);
      const { error } = await supabase.from(CHAT_TABLE).insert({
        nickname,
        content: cleanContent,
        sender_id: getClientId(),
        is_private: Boolean(recipient),
        recipient
      });
      setIsSending(false);
      return !error;
    },
    [currentUser, isSending]
  );

  const addReaction = useCallback(
    async (messageId, emoji) => {
      const nickname = currentUser?.trim();
      if (!nickname) return false;

      const tempId = crypto.randomUUID();
      // Actualización optimista instantánea
      setReactions((current) => [
        ...current,
        {
          id: tempId,
          message_id: messageId,
          nickname,
          emoji,
          created_at: new Date().toISOString()
        }
      ]);

      try {
        const { error } = await supabase.from(REACTIONS_TABLE).insert({
          message_id: messageId,
          nickname,
          emoji
        });

        if (error) {
          // Revertir si la inserción falló (ej: duplicado o tabla no existente)
          setReactions((current) => current.filter((r) => r.id !== tempId));
          return false;
        }
        return true;
      } catch {
        setReactions((current) => current.filter((r) => r.id !== tempId));
        return false;
      }
    },
    [currentUser]
  );

  return {
    messages,
    reactions,
    lastIncomingMessage,
    isSending,
    sendMessage,
    addReaction
  };
}